import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useOrgFeature } from '@/hooks/useOrgFeature';
import { dashboardApi, DashboardStats, LatestWork } from '@/services/dashboardApi';
import { newsroomApi } from '@/services/newsroomApi';
import VideoQuickStart from '@/components/Dashboard/VideoQuickStart';
import {
  MicrophoneIcon,
  FilmIcon,
  UserCircleIcon,
  ClockIcon,
  SpeakerWaveIcon,
  NewspaperIcon,
} from '@heroicons/react/24/outline';

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const StatCard: React.FC<{ label: string; value: string | number; icon: React.ElementType; color: string }> = ({
  label,
  value,
  icon: Icon,
  color,
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
  </div>
);

const LatestItem: React.FC<{
  title: string;
  subtitle: string;
  meta?: string;
  status?: string;
  imageUrl?: string;
  onClick?: () => void;
}> = ({ title, subtitle, meta, status, imageUrl, onClick }) => {
  const getStatusColor = (s?: string) => {
    switch (s) {
      case 'completed':
      case 'ready':
      case 'published':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
      case 'processing':
      case 'training':
      case 'in_review':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
      case 'failed':
      case 'rejected':
        return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div
      onClick={onClick}
      className={`flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
        onClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2 transition-colors' : ''
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {imageUrl && (
          <img
            src={imageUrl}
            alt={title}
            className="h-8 w-8 rounded-full object-cover flex-shrink-0 border border-gray-200 dark:border-gray-600"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{title}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {status && (
          <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(status)}`}>
            {status}
          </span>
        )}
        {meta && <span className="text-xs text-gray-400 dark:text-gray-500">{meta}</span>}
      </div>
    </div>
  );
};

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { isEnabled: isNewsroomEnabled } = useOrgFeature('newsroom');
  const navigate = useNavigate();

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardApi.getStats(),
  });

  const stats = dashboardData?.data?.stats;
  const latest = dashboardData?.data?.latest;

  // Newsroom is an opt-in per-org add-on tracked outside the main dashboard
  // stats aggregation (see useOrgFeature) — same gate the sidebar nav uses,
  // so this column only appears where the "Newsroom" nav item does too.
  const canSeeNewsroom = hasPermission('newsroom', 'read') && isNewsroomEnabled;
  const { data: newsroomData, isLoading: newsroomLoading } = useQuery({
    queryKey: ['dashboard-newsroom-latest'],
    queryFn: () => newsroomApi.getArticles({ limit: 5, sortBy: 'publishedAt', sortOrder: 'desc' }),
    enabled: canSeeNewsroom,
  });
  const latestArticles = newsroomData?.articles ?? [];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Welcome back, {user?.name}!
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Here's what's happening in your workspace today.
        </p>
      </div>

      {/* My Workspace - Stats Section */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">My Workspace</h2>
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-5 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard
              label="Voices Created"
              value={stats?.voicesCount || 0}
              icon={MicrophoneIcon}
              color="bg-purple-500"
            />
            <StatCard
              label="Audios Translated"
              value={stats?.audioTranslationsCount || 0}
              icon={SpeakerWaveIcon}
              color="bg-blue-500"
            />
            <StatCard
              label="Videos Translated"
              value={stats?.videoTranslationsCount || 0}
              icon={FilmIcon}
              color="bg-green-500"
            />
            <StatCard
              label="Audio Duration"
              value={formatDuration(stats?.audioTotalDuration || 0)}
              icon={ClockIcon}
              color="bg-orange-500"
            />
            <StatCard
              label="Video Duration"
              value={formatDuration(stats?.videoTotalDuration || 0)}
              icon={ClockIcon}
              color="bg-pink-500"
            />
          </div>
        )}
      </section>

      {/* Start with a video - guided upload/pick + action CTA */}
      {hasPermission('videos', 'create') && (
        <section className="mb-10">
          <VideoQuickStart />
        </section>
      )}

      {/* Latest Work - 3 Column Section */}
      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Latest Work</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Voices Column */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <MicrophoneIcon className="h-5 w-5 text-purple-500" />
                Voices
              </h3>
              <button
                onClick={() => navigate('/app/audio/voices')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                View all
              </button>
            </div>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse"></div>
                ))}
              </div>
            ) : latest?.voices?.length ? (
              <div>
                {latest.voices.map((voice) => (
                  <LatestItem
                    key={voice.id}
                    title={voice.name}
                    subtitle={voice.language}
                    status={voice.status}
                    meta={formatDate(voice.createdAt)}
                    onClick={() => navigate('/app/audio/voices')}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                No voices created yet
              </p>
            )}
          </div>

          {/* Videos Column */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FilmIcon className="h-5 w-5 text-green-500" />
                Videos
              </h3>
              <button
                onClick={() => navigate('/app/audio/video-translation')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                View all
              </button>
            </div>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse"></div>
                ))}
              </div>
            ) : latest?.videos?.length ? (
              <div>
                {latest.videos.map((video) => (
                  <LatestItem
                    key={video.id}
                    title={video.title || `${video.targetLanguage} Translation`}
                    subtitle={[video.videoMode, video.duration ? formatDuration(video.duration) : null].filter(Boolean).join(' · ')}
                    status={video.status}
                    meta={formatDate(video.createdAt)}
                    onClick={() => navigate('/app/audio/video-translation')}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                No videos translated yet
              </p>
            )}
          </div>

          {/* Speech-to-Text Column */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <MicrophoneIcon className="h-5 w-5 text-purple-500" />
                Speech to Text
              </h3>
              <button
                onClick={() => navigate('/app/audio/speech-to-text')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                View all
              </button>
            </div>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse"></div>
                ))}
              </div>
            ) : latest?.speechToTextJobs?.length ? (
              <div>
                {latest.speechToTextJobs.map((job) => (
                  <LatestItem
                    key={job.id}
                    title={job.title || `${job.language !== 'auto' ? job.language : 'Auto'} Transcription`}
                    subtitle={job.duration ? formatDuration(job.duration) : (job.wordCount ? `${job.wordCount} words` : job.language)}
                    status={job.status}
                    meta={formatDate(job.createdAt)}
                    onClick={() => navigate('/app/audio/speech-to-text')}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                No transcriptions yet
              </p>
            )}
          </div>

          {/* Text-to-Speech Column */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <SpeakerWaveIcon className="h-5 w-5 text-orange-500" />
                Text to Speech
              </h3>
              <button
                onClick={() => navigate('/app/audio/text-to-speech')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                View all
              </button>
            </div>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse"></div>
                ))}
              </div>
            ) : latest?.textToSpeechJobs?.length ? (
              <div>
                {latest.textToSpeechJobs.map((job) => (
                  <LatestItem
                    key={job.id}
                    title={job.title || 'Speech Generation'}
                    subtitle={[job.format?.toUpperCase(), job.duration ? formatDuration(job.duration) : null].filter(Boolean).join(' · ') || '—'}
                    status={job.status}
                    meta={formatDate(job.createdAt)}
                    onClick={() => navigate('/app/audio/text-to-speech')}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                No speech generations yet
              </p>
            )}
          </div>

          {/* Avatars Column */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <UserCircleIcon className="h-5 w-5 text-blue-500" />
                Avatars
              </h3>
              <button
                onClick={() => navigate('/app/audio/avatars')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                View all
              </button>
            </div>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse"></div>
                ))}
              </div>
            ) : latest?.avatars?.length ? (
              <div>
                {latest.avatars.map((avatar) => (
                  <LatestItem
                    key={avatar.id}
                    title={avatar.name}
                    subtitle={avatar.isLibrary ? 'Library' : 'Custom'}
                    meta={formatDate(avatar.createdAt)}
                    imageUrl={avatar.imageUrl}
                    onClick={() => navigate('/app/audio/avatars')}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                No avatars available
              </p>
            )}
          </div>

          {/* Newsroom Column — opt-in add-on, only rendered where the sidebar nav shows it too */}
          {canSeeNewsroom && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <NewspaperIcon className="h-5 w-5 text-indigo-500" />
                  Newsroom
                </h3>
                <button
                  onClick={() => navigate('/app/newsroom')}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  View all
                </button>
              </div>
              {newsroomLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : latestArticles.length ? (
                <div>
                  {latestArticles.map((article) => (
                    <LatestItem
                      key={article._id}
                      title={article.translatedTitle || article.originalTitle}
                      subtitle={article.outlet}
                      status={article.status}
                      meta={formatDate(article.publishedAt)}
                      onClick={() => navigate(`/app/newsroom/articles/${article._id}`)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                  No articles yet
                </p>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
